import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface FeatureRow {
  readonly featureId: string;
  readonly featureCode: string;
  readonly featureName: string;
  readonly featureCategory: string;
  readonly priority: string;
  readonly complexity: string;
  readonly status: string;
}

const CATEGORIES = [
  '', 'FUNCTIONAL', 'TECHNICAL', 'COMPLIANCE', 'UX',
  'INTEGRATION', 'PERFORMANCE', 'SECURITY', 'OTHER',
];
const STATUSES = ['', 'PROPOSED', 'PRIORITIZED', 'APPROVED', 'IN_ROADMAP', 'RELEASED', 'DEFERRED', 'REJECTED'];
const CREATE_CATEGORIES = CATEGORIES.filter(Boolean);

/**
 * M33 — Feature Management Dashboard (§31 / §11 / §12). Register of features
 * (T438) with category, complexity band, weighted-priority band and the
 * propose→prioritize→approve→roadmap→release lifecycle. Supports authoring a
 * feature and driving it — prioritize (weighted factors), approve,
 * add-to-roadmap, release, defer and reject — each wired to the guarded
 * /v1/pm/features write API. Filterable by feature category and status.
 *
 * NOTE (G06 MF-05 / CR-07): exported as `PmFeatureManagementDashboard` to
 * disambiguate from the M43 SaaS `FeatureManagementDashboard`. M43 owns the
 * canonical `FeatureManagementDashboard` name; this PM (M33) variant is aliased.
 */
export function PmFeatureManagementDashboard(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<FeatureRow[]>([]);
  const [featureCategory, setFeatureCategory] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('FUNCTIONAL');
  const [scores, setScores] = useState<Record<string, string>>({});
  const [roadmaps, setRoadmaps] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (featureCategory) params.set('featureCategory', featureCategory);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: FeatureRow[] }>(`/v1/pm/features${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, featureCategory, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? 'You are not authorised to manage features (FEATURE_MANAGE required).'
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError('A feature name is required.'); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/features`, {
        method: 'POST',
        body: { featureName: newName.trim(), featureCategory: newCategory },
        token,
      });
      setNotice(`Created ${newName.trim()}.`);
      setNewName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: FeatureRow, path: string, label: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/features/${r.featureId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(`${r.featureCode}: ${label}.`);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="feature-management-dashboard">
      <h1>Feature Management Dashboard</h1>

      <section aria-label="create-feature">
        <h2>Create Feature</h2>
        <form aria-label="feature-form" onSubmit={(e) => void create(e)}>
          <label>Name<input aria-label="field-feature-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
          <label>Category
            <select aria-label="field-feature-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CREATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-feature" disabled={busy}>{busy ? 'Creating…' : 'Create Feature'}</button>
        </form>
        {actionError && <p role="alert" aria-label="feature-error">{actionError}</p>}
        {notice && <p aria-label="feature-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          Category
          <select aria-label="filter-feature-category" value={featureCategory} onChange={(e) => setFeatureCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || 'All'}</option>)}
          </select>
        </label>
        <label>
          Status
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || 'All'}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="feature-count">{rows.length} feature(s)</p>
          <table aria-label="feature-table">
            <thead><tr><th>Code</th><th>Name</th><th>Category</th><th>Priority</th><th>Complexity</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.featureId} data-status={r.status}>
                  <td>{r.featureCode}</td><td>{r.featureName}</td><td>{r.featureCategory}</td>
                  <td>{r.priority}</td><td>{r.complexity}</td><td>{r.status}</td>
                  <td>
                    {r.status === 'PROPOSED' && (
                      <>
                        <input aria-label={`factor-score-${r.featureCode}`} type="number" value={scores[r.featureId] ?? ''} onChange={(e) => setScores((m) => ({ ...m, [r.featureId]: e.target.value }))} placeholder="Value score" />
                        <button type="button" aria-label={`prioritize-${r.featureCode}`} onClick={() => void act(r, 'prioritize', 'prioritized', { factorScores: [{ factor: 'BUSINESS_VALUE', score: Number(scores[r.featureId] || 0) }] })}>Prioritize</button>
                      </>
                    )}
                    {r.status === 'PRIORITIZED' && (
                      <button type="button" aria-label={`approve-${r.featureCode}`} onClick={() => void act(r, 'approve', 'approved')}>Approve</button>
                    )}
                    {r.status === 'APPROVED' && (
                      <>
                        <input aria-label={`roadmap-id-${r.featureCode}`} value={roadmaps[r.featureId] ?? ''} onChange={(e) => setRoadmaps((m) => ({ ...m, [r.featureId]: e.target.value }))} placeholder="Roadmap id" />
                        <button type="button" aria-label={`add-to-roadmap-${r.featureCode}`} onClick={() => void act(r, 'add-to-roadmap', 'added to roadmap', { roadmapId: roadmaps[r.featureId] ?? '' })}>Add to Roadmap</button>
                      </>
                    )}
                    {r.status === 'IN_ROADMAP' && (
                      <button type="button" aria-label={`release-${r.featureCode}`} onClick={() => void act(r, 'release', 'released')}>Release</button>
                    )}
                    {!['RELEASED', 'DEFERRED', 'REJECTED'].includes(r.status) && (
                      <>
                        <button type="button" aria-label={`defer-${r.featureCode}`} onClick={() => void act(r, 'defer', 'deferred')}>Defer</button>
                        <button type="button" aria-label={`reject-${r.featureCode}`} onClick={() => void act(r, 'reject', 'rejected')}>Reject</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
